import { type ModalId, type ModalViewAction } from '@heidi/schema/ml-scribe';

/**
 * The app modal queue's vocabulary and port seam. TYPES live here rather than
 * in `/contracts` because they name `@heidi/schema` values and the contracts
 * door is relative-import-only — the same footing as the UI-tracking ports.
 *
 * THE QUEUE ITSELF STAYS APP-SIDE (pacing, seen-set, backend POST, dequeue),
 * and crosses exactly two ways: the subscription hook by reference (the
 * consumer calls it unconditionally at its top level) and the launch writes
 * as callbacks. `MODAL_PRIORITY` mirrors the app enum's numeric values; a
 * numeric enum accepts them, so descriptors pass into the app queue without
 * a cast. The wiring suite asserts the values agree.
 */
export const MODAL_PRIORITY = {
  LOW: 1,
  NORMAL: 5,
  HIGH: 10,
  CRITICAL: 20,
  HIGHEST: 30,
  SAFETY_SYSTEM: 40,
} as const;

export type ModalPriorityValue = (typeof MODAL_PRIORITY)[keyof typeof MODAL_PRIORITY];

/** Twin of the app queue's `QueuedModal` descriptor. */
export interface QueuedModalDescriptor {
  id: ModalId;
  priority: ModalPriorityValue;
  dismissible?: boolean;
  showOnlyAfterAccountCreation?: boolean;
  showOnce?: boolean;
}

/** Twin of the close-options the app subscription accepts. */
export interface QueuedModalCloseOptions {
  action?: ModalViewAction;
  increment_count?: boolean;
  hide_forever?: boolean;
  skipPacing?: boolean;
}

/** What `useQueuedModal` returns — the whole surface queue consumers use. */
export interface QueuedModalSubscription {
  isOpen: boolean;
  dismissible: boolean;
  handleClose: () => void;
  onOpenChange: (open: boolean) => void;
  closeWithOptions: (options?: QueuedModalCloseOptions) => void;
}

/** The app modal queue as one injected bundle. */
export interface DictationModalQueuePorts {
  /** The real app hook, supplied BY REFERENCE — rules of hooks apply through it. */
  useQueuedModal: (modalId: ModalId) => QueuedModalSubscription;
  /** Enqueue a launch modal (the queue's pacing applies). */
  enqueueModal: (modal: QueuedModalDescriptor) => void;
  /** Event-time read of the backend-synced seen set; `null` = not hydrated. */
  getSeenModalIds: () => Set<ModalId> | null;
  /** REACTIVE seen-set read (banner gating re-renders on hydration) — a hook
   * by reference, called unconditionally at the consumer's top level. */
  useSeenModalIds: () => Set<ModalId> | null;
  /** Add an id to the seen set (the retire write). */
  markModalSeen: (modalId: ModalId) => void;
}

let ports: DictationModalQueuePorts | null = null;

export const configureDictationModalQueuePorts = (
  next: DictationModalQueuePorts
): void => {
  ports = next;
};

export const getDictationModalQueuePorts = (): DictationModalQueuePorts => {
  if (!ports) {
    throw new Error(
      '[dictation/modal-queue-ports] ports not configured — the app must load ' +
        "'@/utils/dictation-modal-queue-wiring' at bootstrap; tests must call " +
        'configureDictationModalQueuePorts() with a double first.'
    );
  }
  return ports;
};

export const resetDictationModalQueuePortsForTests = (): void => {
  ports = null;
};
